import React from "react";
import { Popover, Button, Tooltip, Divider, Dropdown, Menu, Popconfirm } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  EditOutlined,
  LinkOutlined,
  DisconnectOutlined,
  FontSizeOutlined,
  UnorderedListOutlined as ListIcon,
  DeleteOutlined,
} from "@ant-design/icons";

import "../../styles/bulletinAdmin/components/bulletinBlock.scss";

export default function PopoverToolbar({
  toolbarRef,
  showPopover,
  children,
  // Rich text props
  execCommand,
  activeFormats = {},
  // Align props
  onAlignChange,
  currentAlign,
  // Upload props
  onUploadClick,
  // Link props
  onLinkClick,
  // Font style props
  onFontSizeChange,
  currentFontSize = "body",
  onFontColorChange,
  currentFontColor = "#000000",
  // Control props
  showRichTextControls = false,
  showAlignControls = true,
  showUploadControls = false,
  showImageLinkControl = false,
  showRemoveControl = false,
  currentLink = "",
  // Callbacks
  onRemoveImage,
  // Popover props
  onOpenChange,
}) {
  // Dropdown Menus for various controls
  const listMenu = (
    <Menu>
      <Menu.Item
        key="ul"
        onClick={() => execCommand("insertUnorderedList")}
        icon={<UnorderedListOutlined />}
      >
        Bulleted List
      </Menu.Item>
      <Menu.Item
        key="ol"
        onClick={() => execCommand("insertOrderedList")}
        icon={<OrderedListOutlined />}
      >
        Numbered List
      </Menu.Item>
    </Menu>
  );

  const FONT_SIZES = [
    { key: "h1", label: "Heading 1", px: 36 },
    { key: "h2", label: "Heading 2", px: 28 },
    { key: "h3", label: "Heading 3", px: 22 },
    { key: "body", label: "Body", px: 16 },
    { key: "small", label: "Small", px: 13 },
  ];

  const fontSizeMenu = (
    <Menu
      onClick={({ key }) => onFontSizeChange(key)}
      selectedKeys={[currentFontSize]}
    >
      {FONT_SIZES.map(({ key, label, px }) => (
        <Menu.Item key={key} style={{ fontSize: `${Math.min(px, 18)}px` }}>
          {label}
        </Menu.Item>
      ))}
    </Menu>
  );

  const COLOR_OPTIONS = [
    { key: "#000000", label: "Black",  border: true },
    { key: "#595959", label: "Gray" },
    { key: "#862633", label: "Maroon" },
    { key: "#006644", label: "Green" },
    { key: "#1565C0", label: "Blue" },
    { key: "#E65100", label: "Orange" },
    { key: "#6A1B9A", label: "Purple" },
    { key: "#ffffff", label: "White",  border: true },
  ];

  const fontColorMenu = (
    <Menu
      onClick={({ key }) => onFontColorChange(key)}
      selectedKeys={[currentFontColor]}
    >
      {COLOR_OPTIONS.map(({ key, label, border }) => (
        <Menu.Item key={key}>
          <span
            style={{
              display: "inline-block",
              width: "14px",
              height: "14px",
              borderRadius: "50%",
              backgroundColor: key,
              marginRight: "8px",
              verticalAlign: "middle",
              border: border ? "1px solid #d9d9d9" : "none",
            }}
          />
          {label}
        </Menu.Item>
      ))}
    </Menu>
  );

  // Toolbar content with rich text, alignment, and upload controls
  const richTextContent = showRichTextControls && execCommand && (
    <>
      {/* Font Size */}
      <Tooltip title="Font Size">
        <Dropdown overlay={fontSizeMenu} trigger={["click"]}>
          <Button type="text" className="format-btn" icon={<FontSizeOutlined />} />
        </Dropdown>
      </Tooltip>

      <Divider type="vertical" style={{ margin: "0 2px" }} />

      {/* Inline formatting: Bold, Italic, Underline, Strikethrough */}
      <Tooltip title="Bold">
        <Button
          type="text"
          className={activeFormats.bold ? "format-btn active" : "format-btn"}
          onClick={() => execCommand("bold")}
          icon={<BoldOutlined />}
        />
      </Tooltip>
      <Tooltip title="Italic">
        <Button
          type="text"
          className={activeFormats.italic ? "format-btn active" : "format-btn"}
          onClick={() => execCommand("italic")}
          icon={<ItalicOutlined />}
        />
      </Tooltip>
      <Tooltip title="Underline">
        <Button
          type="text"
          className={activeFormats.underline ? "format-btn active" : "format-btn"}
          onClick={() => execCommand("underline")}
          icon={<UnderlineOutlined />}
        />
      </Tooltip>
      <Tooltip title="Strikethrough">
        <Button
          type="text"
          className={activeFormats.strikethrough ? "format-btn active" : "format-btn"}
          onClick={() => execCommand("strikethrough")}
          icon={<StrikethroughOutlined />}
        />
      </Tooltip>

      <Divider type="vertical" style={{ margin: "0 2px" }} />

      {/* Font Color */}
      <Tooltip title="Font Color">
        <Dropdown overlay={fontColorMenu} trigger={["click"]}>
          <Button type="text" className="format-btn">
            <span
              style={{
                display: "inline-block",
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                backgroundColor: currentFontColor,
                border: "1px solid #ccc",
                verticalAlign: "middle",
              }}
            />
          </Button>
        </Dropdown>
      </Tooltip>

      {/* List Style */}
      <Tooltip title="List Style">
        <Dropdown overlay={listMenu} trigger={["click"]}>
          <Button type="text" className="format-btn" icon={<ListIcon />} />
        </Dropdown>
      </Tooltip>

      {/* Link */}
      {activeFormats.isLink ? (
        <Tooltip title="Unlink">
          <Button
            type="text"
            className="format-btn active"
            onClick={() => execCommand("unlink")}
            icon={<DisconnectOutlined />}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Add Link">
          <Button
            type="text"
            className="format-btn"
            onClick={onLinkClick}
            icon={<LinkOutlined />}
          />
        </Tooltip>
      )}
    </>
  );

  const alignContent = showAlignControls && onAlignChange && (
    <>
      <Tooltip title="Left Align">
        <Button
          type="text"
          className={
            (currentAlign || "left") === "left"
              ? "format-btn active"
              : "format-btn"
          }
          onClick={() => onAlignChange("left")}
          icon={<AlignLeftOutlined />}
        />
      </Tooltip>
      <Tooltip title="Center Align">
        <Button
          type="text"
          className={
            currentAlign === "center" ? "format-btn active" : "format-btn"
          }
          onClick={() => onAlignChange("center")}
          icon={<AlignCenterOutlined />}
        />
      </Tooltip>
      <Tooltip title="Right Align">
        <Button
          type="text"
          className={
            currentAlign === "right" ? "format-btn active" : "format-btn"
          }
          onClick={() => onAlignChange("right")}
          icon={<AlignRightOutlined />}
        />
      </Tooltip>
    </>
  );

  const uploadContent = showUploadControls && onUploadClick && (
    <Tooltip title="Replace">
      <Button
        type="text"
        className="format-btn"
        onClick={onUploadClick}
        icon={<EditOutlined />}
      />
    </Tooltip>
  );

  const imageLinkContent = showImageLinkControl && (
    <Tooltip title={currentLink ? "Edit link" : "Add link"}>
      <Button
        type="text"
        className={currentLink ? "format-btn active" : "format-btn"}
        onClick={onLinkClick}
        icon={<LinkOutlined />}
      />
    </Tooltip>
  );

  const removeContent = showRemoveControl && onRemoveImage && (
    <Popconfirm
      title="Remove this image?"
      description="The file will be permanently deleted from the server."
      onConfirm={onRemoveImage}
      okText="Remove"
      okButtonProps={{ danger: true }}
      cancelText="Cancel"
    >
      <Tooltip title="Remove image">
        <Button
          type="text"
          className="format-btn"
          icon={<DeleteOutlined />}
          danger
        />
      </Tooltip>
    </Popconfirm>
  );

  const controls = [richTextContent, alignContent, uploadContent, imageLinkContent, removeContent].filter(
    Boolean
  );

  if (controls.length === 0) {
    return <>{children}</>;
  }

  const content = (
    <div ref={toolbarRef} style={{ display: "flex", alignItems: "center" }}>
      {controls.map((control, index) => (
        <React.Fragment key={index}>
          {control}
          {index < controls.length - 1 && <Divider type="vertical" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <Popover
      content={content}
      open={showPopover}
      trigger="hover"
      placement="topLeft"
      onOpenChange={onOpenChange}
    >
      {children}
    </Popover>
  );
}
